import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	AlertTriangle,
	Award,
	Building2,
	CheckCircle2,
	Clock,
	Eye,
	Loader2,
} from "lucide-react";
import { VendorLayout } from "@/components/layouts/VendorLayout";
import { useBuyerContext } from "@/contexts/vendor-buyer-context";
import { useTRPC } from "@/integrations/trpc/react";
import { useAuth } from "@/lib/auth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";

export const Route = createFileRoute("/vendor/$buyerSlug/contracts/")({
	component: VendorContractsPage,
});

function getContractStatusColor(status: string) {
	switch (status) {
		case "active":
			return "bg-green-100 text-green-700";
		case "completed":
			return "bg-gray-100 text-gray-700";
		case "pending":
			return "bg-yellow-100 text-yellow-700";
		default:
			return "bg-gray-100 text-gray-700";
	}
}

function VendorContractsPage() {
	const buyer = useBuyerContext();
	const { user } = useAuth();
	const { buyerSlug } = Route.useParams();
	const trpc = useTRPC();

	const vendorEmail = user?.email ?? "";

	// Query contracts from the API
	const { data: contracts, isLoading } = useQuery({
		...trpc.vendorPortal.contracts.list.queryOptions({
			vendorEmail,
			status: "all",
		}),
		enabled: !!vendorEmail,
	});

	const stats = {
		total: (contracts ?? []).length,
		active: (contracts ?? []).filter((c) => c.status === "active").length,
		completed: (contracts ?? []).filter((c) => c.status === "completed").length,
	};

	if (isLoading) {
		return (
			<VendorLayout buyerName={buyer.name} buyerSlug={buyerSlug}>
				<div className="flex items-center justify-center py-12">
					<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				</div>
			</VendorLayout>
		);
	}

	return (
		<VendorLayout buyerName={buyer.name} buyerSlug={buyerSlug}>
			<div className="space-y-6">
				{/* Header */}
				<div>
					<h1 className="text-3xl font-bold tracking-tight">My Contracts</h1>
					<p className="text-muted-foreground">
						View and manage your awarded contracts
					</p>
				</div>

				{/* Stats */}
				<div className="grid gap-4 md:grid-cols-3">
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<Award className="h-4 w-4 text-muted-foreground" />
								<span className="text-sm text-muted-foreground">
									Total Contracts
								</span>
							</div>
							<p className="text-2xl font-bold mt-1">{stats.total}</p>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<CheckCircle2 className="h-4 w-4 text-green-500" />
								<span className="text-sm text-muted-foreground">Active</span>
							</div>
							<p className="text-2xl font-bold mt-1">{stats.active}</p>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<Clock className="h-4 w-4 text-gray-500" />
								<span className="text-sm text-muted-foreground">Completed</span>
							</div>
							<p className="text-2xl font-bold mt-1">{stats.completed}</p>
						</CardContent>
					</Card>
				</div>

				{/* Contracts Table */}
				<Card>
					<CardHeader>
						<CardTitle>Contracts</CardTitle>
						<CardDescription>
							{(contracts ?? []).length} contract
							{(contracts ?? []).length !== 1 ? "s" : ""} found
						</CardDescription>
					</CardHeader>
					<CardContent>
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Contract</TableHead>
									<TableHead>Organization</TableHead>
									<TableHead>Status</TableHead>
									<TableHead>Timeline</TableHead>
									<TableHead className="text-right">Actions</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{(contracts ?? []).map((contract) => (
									<TableRow key={contract.id}>
										<TableCell>
											<div className="font-medium">
												{contract.solicitationTitle}
											</div>
											<div className="text-sm text-muted-foreground">
												Awarded{" "}
												{new Date(contract.awardDate).toLocaleDateString()}
											</div>
										</TableCell>
										<TableCell>
											<div className="flex items-center gap-1 text-sm">
												<Building2 className="h-3 w-3 text-muted-foreground" />
												{contract.organization}
											</div>
										</TableCell>
										<TableCell>
											<Badge
												className={getContractStatusColor(contract.status)}
											>
												{contract.status}
											</Badge>
										</TableCell>
										<TableCell>
											{contract.startDate && contract.endDate ? (
												<div className="space-y-1 w-32">
													<div className="flex items-center justify-between text-xs">
														<span className="text-muted-foreground">
															{contract.status === "completed"
																? "Completed"
																: `${contract.daysRemaining}d left`}
														</span>
														<span>{contract.percentComplete}%</span>
													</div>
													<Progress
														value={contract.percentComplete}
														className="h-1.5"
													/>
													<div className="text-xs text-muted-foreground">
														{new Date(contract.startDate).toLocaleDateString()} -{" "}
														{new Date(contract.endDate).toLocaleDateString()}
													</div>
												</div>
											) : (
												<span className="text-sm text-muted-foreground">
													No dates set
												</span>
											)}
										</TableCell>
										<TableCell className="text-right">
											<Button variant="outline" size="sm" asChild>
												<Link
													to="/vendor/$buyerSlug/contracts/$contractId"
													params={{ buyerSlug, contractId: contract.id }}
												>
													<Eye className="mr-1 h-3 w-3" />
													View
												</Link>
											</Button>
										</TableCell>
									</TableRow>
								))}
								{(contracts ?? []).length === 0 && (
									<TableRow>
										<TableCell
											colSpan={5}
											className="text-center py-8 text-muted-foreground"
										>
											No contracts found
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</CardContent>
				</Card>

				{/* Expiring Soon Alert */}
				{(contracts ?? []).some(
					(c) => c.status === "active" && c.daysRemaining <= 30,
				) && (
					<Card className="border-yellow-200 bg-yellow-50">
						<CardContent className="pt-6">
							<div className="flex items-start gap-3">
								<AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
								<div>
									<h3 className="font-medium text-yellow-800">
										Contracts Expiring Soon
									</h3>
									<p className="text-sm text-yellow-700 mt-1">
										You have{" "}
										{
											(contracts ?? []).filter(
												(c) => c.status === "active" && c.daysRemaining <= 30,
											).length
										}{" "}
										contract(s) expiring within the next 30 days.
									</p>
								</div>
							</div>
						</CardContent>
					</Card>
				)}
			</div>
		</VendorLayout>
	);
}
